import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import useUserAutoSaveForm from "features/User/DynamicForm/useUserAutoSaveForm";
import DynamicUserField from "features/User/DynamicForm/DynamicUserField";
import { invalidateIssueQueries } from "features/Issues/hooks";
import { UserId } from "features/User/models";
import { FieldDef, IssueDetail } from "models/issue";
import { UserUpdateFormValues } from "components/Forms/UserUpdateForm";
import { useUserDetailQuery } from "features/User/hooks";

interface UserFormFieldsProps {
  userId: UserId;
  fieldsDefs: Array<FieldDef<UserUpdateFormValues>>;
  issue: IssueDetail;
}

export default function DynamicUserFormFields({ userId, fieldsDefs, issue }: UserFormFieldsProps) {
  const queryClient = useQueryClient();

  // Fetch user with journey associations since form fields may need buddy, peopleTeamContact, manager
  const { data: user } = useUserDetailQuery(userId, {
    enabled: !!userId,
    staleTime: 0,
    withJourneyAssociations: true,
  });

  const onSuccess = useCallback(() => {
    invalidateIssueQueries(queryClient, issue);
  }, [issue, queryClient]);

  const fields = useMemo(() => fieldsDefs.map((fieldDef) => fieldDef.name), [fieldsDefs]);

  const { form } = useUserAutoSaveForm({
    user,
    fields,
    onSuccess,
  });

  if (!user) return null;

  return (
    <>
      {fieldsDefs.map((fieldDef) => (
        <DynamicUserField
          key={fieldDef.name}
          customLabel={fieldDef.label}
          fieldName={fieldDef.name}
          form={form}
          formFieldProps={{
            overrideHelperSpace: true,
            backgroundColor: "tableUnderlineBorder.opacity16",
            borderTopRadius: "5px",
          }}
        />
      ))}
    </>
  );
}
